<template>
  <div class="mb-2">
    <AppFilter
      :filters="filterConfig"
      :filter-submitting="filterSubmitting"
      :hide-header="true"
      :initial-values="values"
      @filter="handleFilter"
    />
    <DownloadModal
      v-if="(values.dateFrom && values.dateTo) || (values.dropOffDateFrom && values.dropOffDateTo)"
      :start-date="values.dateFrom || values.dropOffDateFrom"
      :end-date="values.dateTo || values.dropOffDateTo"
      @confirm-download="downloadDocument"
    />
  </div>
</template>

<script>
import AppFilter from '@/components/AppFilter/AppFilter.vue';
import DownloadModal from '@/components/DownloadModal.vue';
import { helper } from '@core/mixins/helper';
import { mapGetters } from 'vuex';
import useService from '@/auth/service/useService';
import { checkHasRole } from '@core/utils/roleAccess';
import { ROLES } from '@/constants/roles';

export default {
  name: 'ConsignmentFilter',

  components: {
    AppFilter,
    DownloadModal
  },

  mixins: [helper],

  props: {
    values: {
      type: Object,
      required: true
    },
    filterSubmitting: {
      type: Boolean,
      required: true,
      default: false
    },
    exportMode: {
      type: String,
      default: null
    }
  },

  data() {
    return {
      showDownloadButton: false,
      consignmentStatus: [],
      providerId: [],
      customerOptions: []
    };
  },

  computed: {
    ...mapGetters({
      consignmentType: 'app/consignmentTypes'
    }),

    filterConfig() {
      return [
        {
          id: 'dateRange',
          label: 'Select Date',
          type: 'dateRange',
          dateKeys: {
            dateFrom: 'dateFrom',
            dateTo: 'dateTo',
            dropOffDateFrom: 'dropOffDateFrom',
            dropOffDateTo: 'dropOffDateTo'
          }
        },
        {
          id: 'barcode',
          label: 'Barcode/Reference Number',
          type: 'text',
          placeholder: 'Barcode/Reference Number'
        },
        {
          id: 'consigneeName',
          label: 'Consignee Name Surname',
          type: 'text',
          placeholder: 'Consignee Name Surname',
          formatter: this.nameFormatter
        },
        {
          id: 'status',
          label: 'Status',
          type: 'select',
          multiple: true,
          options: this.consignmentStatusOptions,
          placeholder: 'Make a Choice'
        },
        {
          id: 'provider',
          label: 'Provider',
          type: 'select',
          options: this.providerOptions,
          placeholder: 'Make a Choice'
        },
        ...(checkHasRole([ROLES.ADMIN, ROLES.ACCOUNT_ADMIN])
          ? [
              {
                id: 'customer_codes',
                label: 'Customer Name',
                type: 'select',
                multiple: true,
                options: this.customersOptions,
                placeholder: 'Make a Choice'
              }
            ]
          : [])
      ];
    },

    typeOptions() {
      return (this.consignmentType || []).map(item => ({
        value: item.value,
        label: item.key
      }));
    },

    consignmentStatusOptions() {
      return (this.consignmentStatus || []).map(item => ({
        value: item.value,
        label: item.info
      }));
    },

    providerOptions() {
      return (this.providerId || []).map(item => ({
        value: item.id,
        label: item.name
      }));
    },

    customersOptions() {
      const options = (this.customerOptions || []).map(item => ({
        value: item.customerCode,
        label: item.name
      }));
      options.unshift({
        value: 'ALL',
        label: this.$t('All Customers')
      });
      return options;
    }
  },

  async created() {
    this.consignmentStatus = await this.getConsignmentStatus();
    this.providerId = await this.getProviders({ paginated: false });
    this.customerOptions = await this.getCustomers({ paginated: false });
    await this.$store.dispatch('app/getConsignmentType');

    if (!this.providerOptions) {
      this.providerId = '';
    }
  },

  methods: {
    handleFilter(filters) {
      if (filters.barcode) {
        filters.barcode = filters.barcode.replace(/\s/g, '');
      }

      if (filters && Object.keys(filters).length > 0) {
        this.showDownloadButton = true;
      } else {
        this.showDownloadButton = false;
      }
      this.$emit('filter', filters);
    },

    createConsignment() {
      this.$router.push('/consignments/create');
    },

    downloadDocument({ export_format }) {
      const { dateFrom, dateTo, dropOffDateFrom, dropOffDateTo, customer_codes } = this.values;

      const isAdminExport = this.exportMode === 'admin';

      const start = dateFrom || dropOffDateFrom;
      const end = dateTo || dropOffDateTo;

      if (!start || !end) return;

      const created_at = {
        start: `${start.substring(0, 10)} 00:00`,
        end: `${end.substring(0, 10)} 23:59`
      };

      if (isAdminExport) {
        const customerCodesPayload =
          customer_codes && !customer_codes.includes('ALL') ? customer_codes : [];

        const requestData = {
          export_format,
          customer_codes: customerCodesPayload,
          created_at
        };

        useService
          .adminExportConsignment(requestData)
          .then(exportResponse => {
            this.showSuccessMessage(`${exportResponse.message}`);
          })
          .catch(error => {
            this.showErrorMessage(
              `${error.message || this.$t('Data transmission failed. Please try again.')}`
            );
          })
          .finally(() => {
            this.$emit('export-done');
          });
      } else {
        const requestData = {
          export_format,
          created_at
        };

        useService
          .customerExportConsignment(requestData)
          .then(exportResponse => {
            this.showSuccessMessage(`${exportResponse.message}`);
          })
          .catch(error => {
            this.showErrorMessage(
              `${error.message || this.$t('Data transmission failed. Please try again.')}`
            );
          })
          .finally(() => {
            this.$emit('export-done');
          });
      }
    },

    continueDownload() {
      if (!this.values.dateFrom) {
        this.showErrorMessage(this.$t('Please select a start date'));
        return;
      }
      if (!this.values.dateTo) {
        this.showErrorMessage(this.$t('Please select an end date'));
        return;
      }
      this.$bvModal.show('downloadModal');
    },

    nameFormatter(value) {
      return value.replace(/[^a-zA-ZçÇğĞıİöÖşŞüÜ\s]/g, '');
    }
  }
};
</script>
